import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import FormControl from "@mui/material/FormControl";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

import {
  Autocomplete,
  TextField,
  Typography,
  Checkbox,
  AutocompleteChangeReason,
  Chip
} from "@mui/material";
import { AutoCompleteValues } from "types/AutoCompleteValues";
import { MultiSelectDropdownProps } from "types/MultiSelectDropDownProps";
import { useTranslation } from "react-i18next";

const StyledFormControl = styled(FormControl)`
  margin: 1;
  width: 100%;
`;

const StyledChip = styled(Chip)`
  && {
    font-size: 13px;
    white-space: nowrap;     
  }
`;

const MultiSelectDropdown = ({
  options,
  label,
  onChange,
  selectedValues,
  required,
  width = "100%",
  marginLeft = 0,
  marginBottom = 0,
  concateText = false,
  renderCode = false,
  isLoading = false,
  isPlanningPlant = false,
  isMaintenancePlant = false
}: MultiSelectDropdownProps) => {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const [open, setOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [t,] = useTranslation();

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const handleChange = (
    event: React.SyntheticEvent,
    values: AutoCompleteValues[],
    reason: AutocompleteChangeReason
  ) => {
    if (isPlanningPlant) {
      onChange(values.map((val) => val.planningPlantId ?? ""));
    }
    else if (isMaintenancePlant) {
      onChange(values.map((val) => val.maintenancePlantId ?? ""));
    } else {
      onChange(values.map((val) => val.id ?? ""));
    }

    setFilteredOptions(options);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let updatedOptions: AutoCompleteValues[];
    const inputValue = event.target.value;
    if (renderCode) {
      updatedOptions = options.filter((option) => {
        return option.code?.toLowerCase().includes(inputValue.toLowerCase());
      });
    } else {
      updatedOptions = options.filter((option) => {
        return (
          option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
          option.id.toLowerCase().includes(inputValue.toLowerCase())
        );
      });
    }
    setFilteredOptions(updatedOptions);
  };

  const displayText = (option: AutoCompleteValues) => {
    if (isPlanningPlant) {
      return `${option.planningPlantId} - ${option.name}`;
    } else if (isMaintenancePlant) {
      return `${option.maintenancePlantId} - ${option.name}`;
    } else {
      return concateText ? (renderCode ? option.code : option.id) + "-" + option.name : option.name;
    }
  };

  return (
    <StyledFormControl required={required} error={required && !selectedValues.length}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        limitTags={2}
        options={filteredOptions}
        value={selectedValues}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        loading={isLoading}
        noOptionsText={t('NoOptionsText')}
        loadingText={t('LoadingText')}
        getOptionLabel={(option) => {
          return displayText(option);
        }}
        onChange={handleChange}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <StyledChip
              variant="filled"
              label={displayText(option)}
              // apply custom styles
              {...getTagProps({ index })}
            />
          ))
        }
        sx={{ width: width, marginLeft: marginLeft, marginBottom: marginBottom }}
        renderOption={(props, option, { selected }) => {
          return (
            <li {...props} key={`${option.id}`}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 1 }}
                checked={selected}
              />
              {displayText(option)}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={label}
            variant="outlined"
            error={required && !selectedValues.length}
            InputLabelProps={{
              style: { fontSize: 12 }, // adjust the font size as needed
            }}
            onChange={handleInputChange}
          />
        )}
      />
      {required && !selectedValues.length && (
        <Typography
          fontSize={10}
          fontWeight={400}
          color="red"
          marginLeft={2}
          variant="subtitle2"
        >
          {label} is required
        </Typography>
      )}
    </StyledFormControl>
  );
};

export default MultiSelectDropdown;